import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

type Language = 'de' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  de: {
    'nav.home': 'Start',
    'nav.services': 'Leistungen',
    'nav.calculator': 'Kalkulator',
    'nav.process': 'Prozess',
    'nav.about': 'Über uns',
    'nav.references': 'Referenzen',
    'nav.faq': 'FAQ',
    'nav.contact': 'Kontakt',
    'nav.cta': 'Beratung',
    'hero.badge': 'KI-GESTÜTZTE UNTERNEHMENSEXZELLENZ',
    'hero.title1': 'Maximieren Sie Ihr',
    'hero.title2': 'Business Potenzial',
    'hero.subtitle': 'Wir kombinieren KI-Exzellenz mit strategischer Kostenoptimierung. Prozessautomatisierung trifft auf intelligente Beschaffung.',
    'hero.cta': 'Kostenlose Beratung',
    'hero.learn': 'Unsere Leistungen',
    'hero.stat1': 'Erfolgreich implementierte Projekte',
    'hero.stat2': 'Eingespartes Kapital',
    'hero.stat3': 'Kundenzufriedenheit',
    'services.title': 'Unsere Leistungen',
    'services.subtitle': 'Von KI-Integration bis Kostenoptimierung – wir bieten ganzheitliche Lösungen für Ihr Unternehmen.',
    'calculator.title': 'Ihr Optimierungspotenzial',
    'calculator.subtitle': 'Verschaffen Sie sich einen ersten Überblick über mögliche Optimierungen in Ihrem Unternehmen.',
    'calculator.expenses': 'Ihre monatlichen Ausgaben',
    'calculator.energy': 'Energiekosten',
    'calculator.it': 'IT & Software',
    'calculator.telecom': 'Telekommunikation',
    'calculator.potential': 'Geschätztes Potenzial',
    'calculator.yearly': 'Jährliches Optimierungspotenzial',
    'calculator.corresponds': 'Das entspricht',
    'calculator.of': '% Ihrer Gesamtausgaben',
    'calculator.energy.short': 'Energie',
    'calculator.telecom.short': 'Telekom',
    'calculator.disclaimer': 'Basierend auf durchschnittlichen Benchmark-Daten und Erfahrungswerten aus vergleichbaren Projekten.',
    'calculator.export': 'Export',
    'calculator.share': 'Teilen',
    'calculator.share.copied': 'Link in Zwischenablage kopiert!',
    'calculator.cta': 'Detaillierte Analyse anfragen',
    'calculator.free': 'Unverbindlich und kostenfrei',
    'calculator.pdf.title': 'OptiAI – Optimierungspotenzial-Analyse',
    'calculator.pdf.expenses': 'Monatliche Ausgaben:',
    'calculator.pdf.energy': '- Energiekosten:',
    'calculator.pdf.it': '- IT & Software:',
    'calculator.pdf.telecom': '- Telekommunikation:',
    'calculator.pdf.yearly': 'Geschätztes jährliches Optimierungspotenzial:',
    'calculator.pdf.breakdown': 'Aufschlüsselung:',
    'calculator.pdf.energy2': '- Energie:',
    'calculator.pdf.it2': '- IT & Software:',
    'calculator.pdf.telecom2': '- Telekommunikation:',
    'calculator.pdf.note': 'Diese Analyse basiert auf durchschnittlichen Benchmark-Daten.',
    'calculator.pdf.contact': 'Für eine detaillierte Analyse kontaktieren Sie uns: office@optiai.at',
    'calculator.pdf.created': 'Erstellt am:',
    'process.title': 'Wie wir arbeiten',
    'process.subtitle': 'Ein klarer, strukturierter Prozess für maximale Effizienz und messbare Ergebnisse.',
    'process.learn': 'Mehr erfahren',
    'process.less': 'Weniger anzeigen',
    'process.details': 'Details:',
    'process.step1.title': 'Analyse',
    'process.step1.desc': 'Wir analysieren Ihre aktuellen Prozesse und identifizieren Optimierungspotenziale in KI und Beschaffung.',
    'process.step1.d1': 'Detaillierte Bestandsaufnahme aller Geschäftsprozesse',
    'process.step1.d2': 'Identifikation von KI-Anwendungsfällen',
    'process.step1.d3': 'Analyse der aktuellen Beschaffungskosten',
    'process.step1.d4': 'Potenzialanalyse für Kostenreduktion',
    'process.step2.title': 'Strategie',
    'process.step2.desc': 'Entwicklung einer maßgeschneiderten Strategie für KI-Integration und Kostenreduktion.',
    'process.step2.d1': 'Erstellung einer individuellen KI-Roadmap',
    'process.step2.d2': 'Priorisierung der Optimierungsmaßnahmen',
    'process.step2.d3': 'ROI-Kalkulation für jeden Bereich',
    'process.step2.d4': 'Implementierungsplan mit Meilensteinen',
    'process.step3.title': 'Umsetzung',
    'process.step3.desc': 'Implementierung der KI-Lösungen und Verhandlung optimierter Verträge mit Lieferanten.',
    'process.step3.d1': 'Agile Implementierung der KI-Lösungen',
    'process.step3.d2': 'Verhandlung mit Lieferanten und Partnern',
    'process.step3.d3': 'Integration in bestehende Systeme',
    'process.step3.d4': 'Schulung Ihrer Mitarbeiter',
    'process.step4.title': 'Optimierung',
    'process.step4.desc': 'Kontinuierliche Überwachung, Anpassung und Maximierung Ihrer Einsparungen und Effizienz.',
    'process.step4.d1': 'Monatliche Performance-Reports',
    'process.step4.d2': 'Kontinuierliche Prozessoptimierung',
    'process.step4.d3': 'Testing neuer Ansätze',
    'process.step4.d4': 'Skalierung erfolgreicher Maßnahmen',
    'references.title': 'Erfolgsgeschichten',
    'references.subtitle': 'Unternehmen, die mit uns ihr volles Potenzial entfaltet haben.',
    'references.r1.industry': 'Fertigung',
    'references.r1.result': '€180.000 jährliche Einsparung durch Energievertragsoptimierung',
    'references.r1.quote': 'OptiAI hat nicht nur unsere Energiekosten halbiert, sondern auch unsere Produktionsprozesse durch KI-Automatisierung revolutioniert.',
    'references.r1.position': 'CFO',
    'references.r2.industry': 'Gesundheitswesen',
    'references.r2.result': '60% Zeitersparnis durch KI-Integration',
    'references.r2.quote': 'Der implementierte KI-Assistent bearbeitet 80% unserer Patientenanfragen automatisch. Unser Team kann sich nun auf komplexere Fälle konzentrieren.',
    'references.r2.position': 'Geschäftsführerin',
    'references.r3.industry': 'Logistik',
    'references.r3.result': '€250.000 Einsparung + 40% effizientere Routen',
    'references.r3.quote': 'Die Kombination aus KI-gestützter Routenoptimierung und optimierten Flottenverträgen hat unsere Profitabilität enorm gesteigert.',
    'references.r3.position': 'Operations Director',
    'whyus.title': 'Warum OptiAI?',
    'whyus.subtitle': 'Ihr Partner für KI-Innovation und nachhaltiges Wachstum.',
    'whyus.cta.title': 'Bereit, Ihr Unternehmen zu transformieren?',
    'whyus.cta.subtitle': 'Lassen Sie uns gemeinsam Potenziale identifizieren und nachhaltige Erfolge erzielen.',
    'whyus.cta.button': 'Jetzt Kontakt aufnehmen',
    'contact.title': 'Kontakt aufnehmen',
    'contact.subtitle': 'Lassen Sie uns über Ihre Herausforderungen sprechen und gemeinsam Lösungen entwickeln.',
    'contact.talk': 'Sprechen Sie mit unseren Experten',
    'contact.email': 'Email',
    'contact.phone': 'Telefon',
    'contact.location': 'Standort',
    'contact.free.title': 'Kostenlose Erstberatung',
    'contact.free.text': 'In einem unverbindlichen Gespräch analysieren wir Ihre aktuelle Situation und zeigen konkrete Optimierungspotenziale auf – sowohl im Bereich KI als auch bei der Kostenreduktion.',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.company': 'Unternehmen',
    'contact.form.phone': 'Telefon',
    'contact.form.message': 'Nachricht',
    'contact.form.submit': 'Nachricht senden',
    'contact.form.success': 'Vielen Dank! Wir melden uns in Kürze bei Ihnen.',
    'contact.form.placeholder.name': 'Ihr vollständiger Name',
    'contact.form.placeholder.email': 'ihre.email@firma.de',
    'contact.form.placeholder.company': 'Ihr Unternehmen',
    'contact.form.placeholder.phone': '+43 …',
    'contact.form.placeholder.message': 'Beschreiben Sie Ihr Anliegen...',
    'footer.description': 'Ihr Partner für KI-Integration und strategische Kostenoptimierung. Wir transformieren Unternehmen durch intelligente Technologie und effiziente Beschaffung.',
    'footer.navigation': 'Navigation',
    'footer.company': 'Unternehmen',
    'footer.legal': 'Rechtliches',
    'footer.contact': 'Kontakt',
    'footer.rights': 'Alle Rechte vorbehalten.',
    'footer.whyus': 'Warum wir',
    'footer.impressum': 'Impressum',
    'footer.datenschutz': 'Datenschutz',
    'footer.agb': 'AGB',
    'footer.cookie': 'Cookie-Richtlinie',
    'footer.cookie.settings': 'Cookie-Einstellungen',
    'sticky.services': 'Mehr über unsere Expertise erfahren',
    'sticky.process': 'Unverbindliches Gespräch vereinbaren',
    'sticky.references': 'Ihr Projekt besprechen',
    'sticky.default': 'Bereit für effizientere Prozesse?',
    'sticky.contact': 'Kontakt aufnehmen',
    'sticky.contact.short': 'Kontakt',
    'sticky.close': 'Schließen',
    'exit.title': 'Einen Moment noch',
    'exit.desc': 'Erfahren Sie in einem kurzen Gespräch, wie andere Unternehmen ihre Prozesse optimiert und messbare Erfolge erzielt haben.',
    'exit.offer.title': 'Kostenlose Potenzialanalyse',
    'exit.offer.desc': 'In 30 Minuten analysieren wir Ihre Situation und zeigen konkrete Optimierungsansätze auf – unverbindlich und kostenfrei.',
    'exit.form.name': 'Ihr Name',
    'exit.form.email': 'Ihre E-Mail',
    'exit.form.placeholder.name': 'Max Mustermann',
    'exit.form.placeholder.email': 'max@firma.de',
    'exit.submit': 'Analyse anfragen',
    'exit.dismiss': 'Nein danke, vielleicht später',
    'exit.success.title': 'Vielen Dank!',
    'exit.success.desc': 'Wir melden uns in Kürze bei Ihnen.',
    'cookie.title': 'Hinweis zur lokalen Speicherung',
    'cookie.desc1': 'Diese Website verwendet keine Cookies und kein Tracking. Wir speichern ausschließlich Ihre Zustimmung zu diesem Hinweis sowie Eingaben im Kostenrechner lokal in Ihrem Browser. Diese Daten bleiben auf Ihrem Gerät und werden nicht übertragen.',
    'cookie.accept': 'Verstanden',
    'cookie.reject': 'Verstanden',
    'cookie.privacy': 'Datenschutzerklärung',
    'cookie.policy': 'Cookie-Richtlinie',
    'cookie.and': 'und',
    'faq.title': 'Häufig gestellte Fragen',
    'faq.subtitle': 'Antworten auf die häufigsten Fragen zu KI-Automatisierung, Kostenoptimierung und unserer Zusammenarbeit.',
    'faq.still': 'Noch Fragen?',
    'faq.still.desc': 'Unsere Experten beantworten Ihre individuellen Fragen in einem kostenlosen Erstgespräch.',
    'faq.cta': 'Kostenlose Beratung anfragen',
  },
  en: {
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.calculator': 'Calculator',
    'nav.process': 'Process',
    'nav.about': 'About Us',
    'nav.references': 'References',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact',
    'nav.cta': 'Consultation',
    'hero.badge': 'AI-POWERED BUSINESS EXCELLENCE',
    'hero.title1': 'Maximize Your',
    'hero.title2': 'Business Potential',
    'hero.subtitle': 'We combine AI excellence with strategic cost optimization. Process automation meets intelligent procurement.',
    'hero.cta': 'Free Consultation',
    'hero.learn': 'Our Services',
    'hero.stat1': 'Successfully Implemented Projects',
    'hero.stat2': 'Capital Saved',
    'hero.stat3': 'Customer Satisfaction',
    'services.title': 'Our Services',
    'services.subtitle': 'From AI integration to cost optimization – we offer comprehensive solutions for your company.',
    'calculator.title': 'Your Optimization Potential',
    'calculator.subtitle': 'Get an initial overview of possible optimizations in your company.',
    'calculator.expenses': 'Your Monthly Expenses',
    'calculator.energy': 'Energy Costs',
    'calculator.it': 'IT & Software',
    'calculator.telecom': 'Telecommunications',
    'calculator.potential': 'Estimated Potential',
    'calculator.yearly': 'Annual Optimization Potential',
    'calculator.corresponds': 'That equals',
    'calculator.of': '% of your total expenses',
    'calculator.energy.short': 'Energy',
    'calculator.telecom.short': 'Telecom',
    'calculator.disclaimer': 'Based on average benchmark data and experience from comparable projects.',
    'calculator.export': 'Export',
    'calculator.share': 'Share',
    'calculator.share.copied': 'Link copied to clipboard!',
    'calculator.cta': 'Request Detailed Analysis',
    'calculator.free': 'Non-binding and free of charge',
    'calculator.pdf.title': 'OptiAI – Optimization Potential Analysis',
    'calculator.pdf.expenses': 'Monthly Expenses:',
    'calculator.pdf.energy': '- Energy Costs:',
    'calculator.pdf.it': '- IT & Software:',
    'calculator.pdf.telecom': '- Telecommunications:',
    'calculator.pdf.yearly': 'Estimated Annual Optimization Potential:',
    'calculator.pdf.breakdown': 'Breakdown:',
    'calculator.pdf.energy2': '- Energy:',
    'calculator.pdf.it2': '- IT & Software:',
    'calculator.pdf.telecom2': '- Telecommunications:',
    'calculator.pdf.note': 'This analysis is based on average benchmark data.',
    'calculator.pdf.contact': 'For a detailed analysis contact us: office@optiai.at',
    'calculator.pdf.created': 'Created on:',
    'process.title': 'How We Work',
    'process.subtitle': 'A clear, structured process for maximum efficiency and measurable results.',
    'process.learn': 'Learn More',
    'process.less': 'Show Less',
    'process.details': 'Details:',
    'process.step1.title': 'Analysis',
    'process.step1.desc': 'We analyze your current processes and identify optimization potentials in AI and procurement.',
    'process.step1.d1': 'Detailed inventory of all business processes',
    'process.step1.d2': 'Identification of AI use cases',
    'process.step1.d3': 'Analysis of current procurement costs',
    'process.step1.d4': 'Potential analysis for cost reduction',
    'process.step2.title': 'Strategy',
    'process.step2.desc': 'Development of a tailored strategy for AI integration and cost reduction.',
    'process.step2.d1': 'Creation of an individual AI roadmap',
    'process.step2.d2': 'Prioritization of optimization measures',
    'process.step2.d3': 'ROI calculation for each area',
    'process.step2.d4': 'Implementation plan with milestones',
    'process.step3.title': 'Implementation',
    'process.step3.desc': 'Implementation of AI solutions and negotiation of optimized contracts with suppliers.',
    'process.step3.d1': 'Agile implementation of AI solutions',
    'process.step3.d2': 'Negotiation with suppliers and partners',
    'process.step3.d3': 'Integration into existing systems',
    'process.step3.d4': 'Training of your employees',
    'process.step4.title': 'Optimization',
    'process.step4.desc': 'Continuous monitoring, adjustment, and maximization of your savings and efficiency.',
    'process.step4.d1': 'Monthly performance reports',
    'process.step4.d2': 'Continuous process optimization',
    'process.step4.d3': 'Testing of new approaches',
    'process.step4.d4': 'Scaling of successful measures',
    'references.title': 'Success Stories',
    'references.subtitle': 'Companies that have unlocked their full potential with us.',
    'references.r1.industry': 'Manufacturing',
    'references.r1.result': '€180,000 annual savings through energy contract optimization',
    'references.r1.quote': 'OptiAI not only halved our energy costs but also revolutionized our production processes through AI automation.',
    'references.r1.position': 'CFO',
    'references.r2.industry': 'Healthcare',
    'references.r2.result': '60% time savings through AI integration',
    'references.r2.quote': 'The implemented AI assistant handles 80% of our patient inquiries automatically. Our team can now focus on more complex cases.',
    'references.r2.position': 'Managing Director',
    'references.r3.industry': 'Logistics',
    'references.r3.result': '€250,000 savings + 40% more efficient routes',
    'references.r3.quote': 'The combination of AI-powered route optimization and optimized fleet contracts has enormously increased our profitability.',
    'references.r3.position': 'Operations Director',
    'whyus.title': 'Why OptiAI?',
    'whyus.subtitle': 'Your partner for AI innovation and sustainable growth.',
    'whyus.cta.title': 'Ready to transform your business?',
    'whyus.cta.subtitle': 'Let us identify potentials together and achieve sustainable success.',
    'whyus.cta.button': 'Contact Us Now',
    'contact.title': 'Get in Touch',
    'contact.subtitle': 'Let us discuss your challenges and develop solutions together.',
    'contact.talk': 'Speak with our experts',
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.location': 'Location',
    'contact.free.title': 'Free Initial Consultation',
    'contact.free.text': 'In a non-binding conversation, we analyze your current situation and identify concrete optimization potentials – both in AI and cost reduction.',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.company': 'Company',
    'contact.form.phone': 'Phone',
    'contact.form.message': 'Message',
    'contact.form.submit': 'Send Message',
    'contact.form.success': 'Thank you! We will contact you shortly.',
    'contact.form.placeholder.name': 'Your full name',
    'contact.form.placeholder.email': 'your.email@company.com',
    'contact.form.placeholder.company': 'Your company',
    'contact.form.placeholder.phone': '+1 …',
    'contact.form.placeholder.message': 'Describe your needs...',
    'footer.description': 'Your partner for AI integration and strategic cost optimization. We transform companies through intelligent technology and efficient procurement.',
    'footer.navigation': 'Navigation',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
    'footer.whyus': 'Why Us',
    'footer.impressum': 'Legal Notice',
    'footer.datenschutz': 'Privacy Policy',
    'footer.agb': 'Terms',
    'footer.cookie': 'Cookie Policy',
    'footer.cookie.settings': 'Cookie Settings',
    'sticky.services': 'Learn more about our expertise',
    'sticky.process': 'Schedule a non-binding consultation',
    'sticky.references': 'Discuss your project',
    'sticky.default': 'Ready for more efficient processes?',
    'sticky.contact': 'Get in Touch',
    'sticky.contact.short': 'Contact',
    'sticky.close': 'Close',
    'exit.title': 'Wait a moment',
    'exit.desc': 'Learn in a short conversation how other companies have optimized their processes and achieved measurable success.',
    'exit.offer.title': 'Free Potential Analysis',
    'exit.offer.desc': 'In 30 minutes, we analyze your situation and show concrete optimization approaches – non-binding and free of charge.',
    'exit.form.name': 'Your Name',
    'exit.form.email': 'Your Email',
    'exit.form.placeholder.name': 'John Smith',
    'exit.form.placeholder.email': 'john@company.com',
    'exit.submit': 'Request Analysis',
    'exit.dismiss': 'No thanks, maybe later',
    'exit.success.title': 'Thank you!',
    'exit.success.desc': 'We will contact you shortly.',
    'cookie.title': 'Local Storage Notice',
    'cookie.desc1': 'This website uses no cookies and no tracking. We only store your consent to this notice and calculator inputs locally in your browser. This data stays on your device and is never transmitted.',
    'cookie.accept': 'Got it',
    'cookie.reject': 'Got it',
    'cookie.privacy': 'Privacy Policy',
    'cookie.policy': 'Cookie Policy',
    'cookie.and': 'and',
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Answers to the most common questions about AI automation, cost optimization, and how we work together.',
    'faq.still': 'Still have questions?',
    'faq.still.desc': 'Our experts answer your individual questions in a free initial consultation.',
    'faq.cta': 'Request Free Consultation',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathIsEn = location.pathname.startsWith('/en');
  const [language, setLanguageState] = useState<Language>(pathIsEn ? 'en' : 'de');

  useEffect(() => {
    setLanguageState(pathIsEn ? 'en' : 'de');
  }, [pathIsEn]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('optiaiLanguage', lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.de] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
